import { action } from 'satcheljs';

import type { TraceErrorObject } from 'owa-analytics-types';
import type { UTPlaceAdminRoles } from '@outlook/unified_telemetry';
import type {
    PlacesAnalyticsAppLoadActionScenarios,
    PlacesAnalyticsPerfEventNames,
    PlacesAnalyticsTabIds,
    PlacesAnalyticsTabLoadActionScenarios,
    PlacesLoaderSources,
    PlacesManagementLoadActionScenarios,
    PlacesRootComponentSources,
} from '../types';

/** This action is called when specific components are mounted that is used for telemetry purposes. */
export interface PLACES_FWK_ACTION_ROOT_COMPONENT_MOUNTEDActionMessage {
    rootComponentName: PlacesRootComponentSources;
    errorObject: TraceErrorObject | undefined;
}
export const placesFwkRootComponentMountedAction: (
    rootComponentName: PlacesRootComponentSources,
    errorObject?: TraceErrorObject
) => PLACES_FWK_ACTION_ROOT_COMPONENT_MOUNTEDActionMessage = action(
    'PLACES_FWK_ACTION_ROOT_COMPONENT_MOUNTED',
    (
        rootComponentName: PlacesRootComponentSources,
        errorObject?: TraceErrorObject
    ): PLACES_FWK_ACTION_ROOT_COMPONENT_MOUNTEDActionMessage => ({
        rootComponentName,
        errorObject,
    })
);

/** This action is called when a check point is reached  at the route loaders */
export interface PlacesLoaderActionActionMessage {
    loaderName: 'PlacesWorkplanLoader';
}
export const placesLoaderAction: (
    loaderName: PlacesLoaderSources
) => PlacesLoaderActionActionMessage = action(
    'PlacesLoaderAction',
    (loaderName: PlacesLoaderSources): PlacesLoaderActionActionMessage => ({
        loaderName,
    })
);

/** This action is called when a page completes loading
 * @param page - The page that has completed loading or specific route
 * @param status - Whether the page loading has started or ended
 * @param result - The status of the page load (Success, Empty, Error, NoAccess, NotFound) - Or anything else required for logging/perf
 * @param errorMessage - Additional information about the failure
 */
export interface PlacesFwkPageLoadActionActionMessage {
    page: string;
    status: 'start' | 'end';
    result: string;
    errorMessage: string | undefined;
}
export const placesFwkPageLoadAction: (
    page: string,
    status: 'start' | 'end',
    result: string,
    errorMessage?: string
) => PlacesFwkPageLoadActionActionMessage = action(
    'PlacesFwkPageLoadAction',
    (
        page: string,
        status: 'start' | 'end',
        result: string,
        errorMessage?: string
    ): PlacesFwkPageLoadActionActionMessage => ({
        page,
        status,
        result,
        errorMessage,
    })
);

export interface PlacesAnalyticsAppLoadActionActionMessage {
    scenario: PlacesAnalyticsAppLoadActionScenarios;
    errorSource: string | undefined;
    customData: any;
    errorObject: TraceErrorObject | undefined;
}
export const placesAnalyticsAppLoadAction: ({
    scenario,
    customData,
    errorSource,
    errorObject,
}: {
    scenario: PlacesAnalyticsAppLoadActionScenarios;
    /* eslint-disable-next-line owa-custom-rules/no-optional-any-parameter -- (https://aka.ms/OWALintWiki)
     * DO NOT COPY-PASTE! This code should be fixed by any developer touching this code
     *	> Optional object properties should not have type "any". This can hide undefined/null references otherwise detectable by the transpiler. */
    customData?: any;
    errorSource?: string;
    errorObject?: TraceErrorObject;
}) => PlacesAnalyticsAppLoadActionActionMessage = action(
    'PlacesAnalyticsAppLoadAction',
    ({
        scenario,
        customData,
        errorSource,
        errorObject,
    }: {
        scenario: PlacesAnalyticsAppLoadActionScenarios;
        /* eslint-disable-next-line owa-custom-rules/no-optional-any-parameter -- (https://aka.ms/OWALintWiki)
         * DO NOT COPY-PASTE! This code should be fixed by any developer touching this code
         *	> Optional object properties should not have type "any". This can hide undefined/null references otherwise detectable by the transpiler. */
        customData?: any;
        errorSource?: string;
        errorObject?: TraceErrorObject;
    }): PlacesAnalyticsAppLoadActionActionMessage => ({
        scenario,
        errorSource,
        customData,
        errorObject,
    })
);

export interface PlacesAnalyticsTabLoadActionActionMessage {
    scenario: PlacesAnalyticsTabLoadActionScenarios;
    tabId: PlacesAnalyticsTabIds;
    prevTabId: PlacesAnalyticsTabIds | undefined;
    errorSource: string | undefined;
    customData: any;
    errorObject: TraceErrorObject | undefined;
}
export const placesAnalyticsTabLoadAction: ({
    scenario,
    tabId,
    prevTabId,
    customData,
    errorSource,
    errorObject,
}: {
    scenario: PlacesAnalyticsTabLoadActionScenarios;
    tabId: PlacesAnalyticsTabIds;
    prevTabId?: PlacesAnalyticsTabIds;
    /* eslint-disable-next-line owa-custom-rules/no-optional-any-parameter -- (https://aka.ms/OWALintWiki)
     * DO NOT COPY-PASTE! This code should be fixed by any developer touching this code
     *	> Optional object properties should not have type "any". This can hide undefined/null references otherwise detectable by the transpiler. */
    customData?: any;
    errorSource?: string;
    errorObject?: TraceErrorObject;
}) => PlacesAnalyticsTabLoadActionActionMessage = action(
    'PlacesAnalyticsTabLoadAction',
    ({
        scenario,
        tabId,
        prevTabId,
        customData,
        errorSource,
        errorObject,
    }: {
        scenario: PlacesAnalyticsTabLoadActionScenarios;
        tabId: PlacesAnalyticsTabIds;
        prevTabId?: PlacesAnalyticsTabIds;
        /* eslint-disable-next-line owa-custom-rules/no-optional-any-parameter -- (https://aka.ms/OWALintWiki)
         * DO NOT COPY-PASTE! This code should be fixed by any developer touching this code
         *	> Optional object properties should not have type "any". This can hide undefined/null references otherwise detectable by the transpiler. */
        customData?: any;
        errorSource?: string;
        errorObject?: TraceErrorObject;
    }): PlacesAnalyticsTabLoadActionActionMessage => ({
        scenario,
        tabId,
        prevTabId,
        errorSource,
        customData,
        errorObject,
    })
);

export interface CancelPlacesAnalyticsPerfEventsActionActionMessage {
    scenario: string;
    eventsToCancel: (PlacesAnalyticsPerfEventNames | 'All')[];
}
export const cancelPlacesAnalyticsPerfEventsAction: ({
    scenario,
    eventsToCancel,
}: {
    scenario: string; // to be updated
    eventsToCancel: (PlacesAnalyticsPerfEventNames | 'All')[];
}) => CancelPlacesAnalyticsPerfEventsActionActionMessage = action(
    'CancelPlacesAnalyticsPerfEventsAction',
    ({
        scenario,
        eventsToCancel,
    }: {
        scenario: string; // to be updated
        eventsToCancel: (PlacesAnalyticsPerfEventNames | 'All')[];
    }): CancelPlacesAnalyticsPerfEventsActionActionMessage => ({
        scenario,
        eventsToCancel,
    })
);

export interface PlacesManagementLoadActionActionMessage {
    scenario: PlacesManagementLoadActionScenarios;
    errorObject: TraceErrorObject | undefined;
    adminRoles: UTPlaceAdminRoles | undefined;
}
export const placesManagementLoadAction: ({
    scenario,
    errorObject,
}: {
    scenario: PlacesManagementLoadActionScenarios;
    errorObject?: TraceErrorObject;
    adminRoles?: UTPlaceAdminRoles;
}) => PlacesManagementLoadActionActionMessage = action(
    'PlacesManagementAppLoadAction',
    ({
        scenario,
        errorObject,
        adminRoles,
    }: {
        scenario: PlacesManagementLoadActionScenarios;
        errorObject?: TraceErrorObject;
        adminRoles?: UTPlaceAdminRoles;
    }): PlacesManagementLoadActionActionMessage => ({
        scenario,
        errorObject,
        adminRoles,
    })
);
